import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Table,
  InputNumber,
  Typography,
  Divider,
  Alert,
  Tag,
  Tooltip,
  message,
  Modal,
  AutoComplete,
  Descriptions,
  Spin
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  SaveOutlined,
  SendOutlined,
  FileTextOutlined,
  BarcodeOutlined,
  CalculatorOutlined,
  UserOutlined,
  ShopOutlined,
  TruckOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
  MailOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import apiService from '../../../../services/apiService';
import moment from 'moment';
import type { SalesOrder, SalesOrderItem, Customer, Product, Warehouse } from '../../types';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface SalesOrderBuilderProps {
  salesOrder?: SalesOrder;
  onSave?: (order: SalesOrder) => void;
  onCancel?: () => void;
  mode?: 'create' | 'edit' | 'view';
}

export const SalesOrderBuilder: React.FC<SalesOrderBuilderProps> = ({
  salesOrder,
  onSave,
  onCancel,
  mode = 'create'
}) => {
  const [form] = Form.useForm();
  const [orderItems, setOrderItems] = useState<SalesOrderItem[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [loading, setLoading] = useState(false);
  const [productSearchModalVisible, setProductSearchModalVisible] = useState(false);
  const [searchProduct, setSearchProduct] = useState('');
  const [productLoading, setProductLoading] = useState(false);
  const [productCache, setProductCache] = useState<Record<string, Product[]>>({});
  const [invoiceGenerated, setInvoiceGenerated] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState<{
    invoiceNumber: string;
    generatedAt: Date;
    amount: number;
    dueDate: Date;
    status: 'draft' | 'sent' | 'paid' | 'overdue';
  } | null>(null);
  const [invoiceViewVisible, setInvoiceViewVisible] = useState(false);

  useEffect(() => {
    // 併發載入三類資料，縮短等待時間
    const auth = localStorage.getItem('auth-storage');
    const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
    const companyId = parsed?.state?.user?.companyId || localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
    const loadCustomers = async () => {
      try {
        // Use lightweight dropdown endpoint (limit 500, returns only essential fields)
        const resp: any = await apiService.get('/customers/dropdown');
        const rows: any[] = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp?.customers) ? resp.customers : (Array.isArray(resp) ? resp : []));
        setCustomers((rows||[]).map((c: any) => ({
          id: String(c.id || c.customerId || ''),
          name: c.name || c.customerName || '',
          email: c.email || '',
          phone: c.phone || c.contact || '',
          address: c.address || '',
          city: c.city || '',
          country: c.country || '',
          company: c.company || '',
          contactPerson: c.contactPerson || '',
          paymentTerms: c.paymentTerms || 'Net 30',
          creditLimit: c.creditLimit || 0,
          taxId: c.taxId || '',
          companyId: c.companyId || c.company?.id
        } as any)));
      } catch { setCustomers([]); }
    };
    const loadProducts = async () => {
      try {
        const respP: any = await apiService.getProductsV2({ page: 1, limit: 200, ...(companyId ? { companyId } : {}) });
        const rowsP: any[] = (
          Array.isArray(respP?.products) ? respP.products :
          Array.isArray(respP?.data?.products) ? respP.data.products :
          Array.isArray(respP?.data) ? respP.data :
          Array.isArray(respP?.rows) ? respP.rows :
          Array.isArray(respP?.items) ? respP.items :
          Array.isArray(respP?.data?.rows) ? respP.data.rows :
          Array.isArray(respP?.data?.items) ? respP.data.items :
          (Array.isArray(respP) ? respP : [])
        );
        setProducts((rowsP||[]).map((p: any) => ({
          id: String(p.id || p.productId),
          sku: p.itemCode || p.sku,
          name: p.itemName || p.name,
          description: p.description || '',
          unitPrice: Number(p.unitPrice || p.price || 0),
          category: p.category || '',
          stockQuantity: p.stockQuantity || 0,
          warehouse: p.warehouse || '',
          uom: p.baseUOM || p.uom || '',
          barcode: p.barcode || '',
          taxRate: 0.1
        })));
      } catch { setProducts([]); }
    };
    const loadWarehouses = async () => {
      try {
        const respW: any = await apiService.getWarehouses({ page: 1, limit: 200, ...(companyId ? { companyId } : {}) });
        const rowsW: any[] = Array.isArray(respW?.data) ? respW.data : (Array.isArray(respW) ? respW : []);
        setWarehouses((rowsW||[]).map((w: any) => ({
          id: String(w.id || w.warehouseId),
          name: w.name || w.warehouseName || '',
          code: w.code || w.warehouseCode || '',
          address: w.address || '',
          manager: w.manager || w.contactPerson || '',
          isActive: Boolean(w.isActive ?? true)
        })));
      } catch { setWarehouses([]); }
    };
    Promise.allSettled([loadCustomers(), loadProducts(), loadWarehouses()]);

    // If editing or viewing existing order, populate form（不再使用示例資料）
    if (salesOrder && (mode === 'edit' || mode === 'view')) {
      form.setFieldsValue({
        orderNumber: salesOrder.orderNumber,
        customerId: salesOrder.customerId,
        orderDate: moment(salesOrder.orderDate),
        deliveryDate: salesOrder.deliveryDate ? moment(salesOrder.deliveryDate) : null,
        warehouseId: salesOrder.warehouseId,
        paymentTerms: salesOrder.paymentTerms,
        priority: salesOrder.priority,
        notes: salesOrder.notes,
        terms: salesOrder.terms
      });
      setOrderItems(salesOrder.items || []);
      setSelectedCustomer(salesOrder.customer);
      setSelectedWarehouse(salesOrder.warehouse);
    } else if (mode === 'create') {
      // For create mode, set default values
      form.setFieldsValue({
        orderNumber: `SO-${Date.now()}`,
        orderDate: moment(),
        paymentTerms: 'Net 30',
        priority: 'normal'
      });
      setOrderItems([]);
      setSelectedCustomer(null);
      setSelectedWarehouse(null);
    }
  }, [salesOrder, mode, form]);

  // 懶載入產品：打開彈窗或搜索變更時，帶搜索詞去後端查詢（去抖）
  useEffect(() => {
    if (!productSearchModalVisible) return;
    const handler = setTimeout(async () => {
      const keyword = (searchProduct || '').trim();
      // 命中快取
      if (productCache[keyword] !== undefined) {
        setProducts(productCache[keyword]);
        return;
      }
      try {
        setProductLoading(true);
        const auth = localStorage.getItem('auth-storage');
        const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
        const companyId = parsed?.state?.user?.companyId || localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
        const resp: any = await apiService.getProductsV2({ page: 1, limit: 10, ...(companyId ? { companyId } : {}), ...(keyword ? { search: keyword } : {}) });
        const rows: any[] = (
          Array.isArray(resp?.products) ? resp.products :
          Array.isArray(resp?.data?.products) ? resp.data.products :
          Array.isArray(resp?.data) ? resp.data :
          Array.isArray(resp) ? resp : []
        );
        const mapped: Product[] = (rows || []).map((p: any) => ({
          id: String(p.id || p.productId),
          sku: p.itemCode || p.sku,
          alternateSkuList: p.alternateSkuList || [],
          name: p.itemName || p.name,
          description: p.description || '',
          unitPrice: Number(p.unitPrice || p.price || 0),
          category: p.category || '',
          stockQuantity: p.stockQuantity || 0,
          warehouse: p.warehouse || '',
          uom: p.baseUOM || p.uom || '',
          barcode: p.barcode || '',
          taxRate: 0.1
        }));
        setProducts(mapped);
        setProductCache(prev => ({ ...prev, [keyword]: mapped }));
      } catch {
        setProducts([]);
      } finally {
        setProductLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [productSearchModalVisible, searchProduct]);

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer || null);
    if (customer) {
      form.setFieldsValue({
        paymentTerms: customer.paymentTerms || 'Net 30'
      });
    }
  };

  const handleWarehouseChange = (warehouseId: string) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    setSelectedWarehouse(warehouse || null);
  };

  const addProduct = (product: Product) => {
    const existingItem = orderItems.find(item => item.productId === product.id);
    
    if (existingItem) {
      // Increase quantity if product already exists
      const updatedItems = orderItems.map(item =>
        item.productId === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      setOrderItems(updatedItems);
    } else {
      // Add new item
      const newItem: SalesOrderItem = {
        id: Date.now().toString(),
        productId: product.id,
        product,
        quantity: 1,
        unitPrice: product.unitPrice,
        discount: 0,
        discountType: 'percentage',
        taxRate: product.taxRate || 0.1,
        lineTotal: product.unitPrice
      };
      setOrderItems([...orderItems, newItem]);
    }
    setProductSearchModalVisible(false);
    message.success(`Added ${product.name} to order`);
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    const updatedItems = orderItems.map(item =>
      item.id === itemId
        ? { 
            ...item, 
            quantity: Math.max(0, quantity),
            lineTotal: calculateLineTotal(item.unitPrice, Math.max(0, quantity), item.discount, item.discountType, item.taxRate)
          }
        : item
    );
    setOrderItems(updatedItems);
  };

  const updateItemPrice = (itemId: string, unitPrice: number) => {
    const updatedItems = orderItems.map(item =>
      item.id === itemId
        ? { 
            ...item, 
            unitPrice: Math.max(0, unitPrice),
            lineTotal: calculateLineTotal(Math.max(0, unitPrice), item.quantity, item.discount, item.discountType, item.taxRate)
          }
        : item
    );
    setOrderItems(updatedItems);
  };

  const updateItemDiscount = (itemId: string, discount: number, discountType: 'percentage' | 'fixed') => {
    const updatedItems = orderItems.map(item =>
      item.id === itemId
        ? { 
            ...item, 
            discount: Math.max(0, discount),
            discountType,
            lineTotal: calculateLineTotal(item.unitPrice, item.quantity, Math.max(0, discount), discountType, item.taxRate)
          }
        : item
    );
    setOrderItems(updatedItems);
  };

  const removeItem = (itemId: string) => {
    setOrderItems(orderItems.filter(item => item.id !== itemId));
  };

  const calculateLineTotal = (unitPrice: number, quantity: number, discount: number, discountType: 'percentage' | 'fixed', taxRate: number): number => {
    const subtotal = unitPrice * quantity;
    const discountAmount = discountType === 'percentage' ? subtotal * (discount / 100) : discount;
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = afterDiscount * taxRate;
    return afterDiscount + taxAmount;
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
    const totalDiscount = orderItems.reduce((sum, item) => {
      const itemSubtotal = item.unitPrice * item.quantity;
      return sum + (item.discountType === 'percentage' ? itemSubtotal * (item.discount / 100) : item.discount);
    }, 0);
    const afterDiscount = subtotal - totalDiscount;
    const taxAmount = orderItems.reduce((sum, item) => {
      const itemSubtotal = item.unitPrice * item.quantity;
      const itemDiscount = item.discountType === 'percentage' ? itemSubtotal * (item.discount / 100) : item.discount;
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      return sum + (itemAfterDiscount * item.taxRate);
    }, 0);
    const total = afterDiscount + taxAmount;

    return {
      subtotal,
      discountAmount: totalDiscount,
      taxAmount,
      totalAmount: total
    };
  };

  const handleSave = async (status: 'draft' | 'confirmed') => {
    try {
      setLoading(true);
      await form.validateFields();
      
      if (orderItems.length === 0) {
        message.error('請至少添加一個產品項目');
        return;
      }

      const formValues = form.getFieldsValue();
      const totals = calculateTotals();
      // 後端需要：customerId、userId、companyId、items[]
      const auth = localStorage.getItem('auth-storage');
      const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
      const userId = parsed?.state?.user?.id || localStorage.getItem('current-user-id') || localStorage.getItem('userId');
      const companyId = (selectedCustomer as any)?.companyId || parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || localStorage.getItem('companyId');
      if (!userId || !companyId) {
        message.error('缺少 userId/companyId，無法保存');
        return;
      }

      const payload = {
        customerId: formValues.customerId,
        userId,
        companyId,
        warehouseId: formValues.warehouseId,
        deliveryDate: formValues.deliveryDate ? formValues.deliveryDate.toISOString() : undefined,
        notes: formValues.notes,
        terms: formValues.terms,
        source: formValues.salesPlatform || 'manual',
        items: orderItems.map(it => ({ productId: it.productId, quantity: it.quantity, unitPrice: it.unitPrice, discount: it.discount, discountType: it.discountType, taxRate: it.taxRate }))
      };

      // 編輯模式應該更新，而不是新增
      let resp: any;
      if (mode === 'edit' && salesOrder?.id) {
        resp = await apiService.updateSalesOrderV2(salesOrder.id, payload);
      } else {
        resp = await apiService.createSalesOrderV2(payload);
      }
      message.success('銷售訂單已保存');
      if (onSave) onSave(resp || payload);
    } catch (error) {
      console.error('Save error:', error);
      message.error('保存銷售訂單失敗');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintInvoice = () => {
    if (!generatedInvoice) {
      message.error('沒有可列印的發票');
      return;
    }

    // Create printable content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>發票 - ${generatedInvoice.invoiceNumber}</title>
        <style>
          body { 
            font-family: "Microsoft JhengHei", Arial, sans-serif; 
            line-height: 1.6; 
            margin: 0; 
            padding: 20px;
            color: #333;
          }
          .invoice-header { 
            border-bottom: 3px solid #1890ff; 
            padding-bottom: 20px; 
            margin-bottom: 30px;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }
          .invoice-title { 
            font-size: 32px; 
            font-weight: bold; 
            color: #1890ff; 
            margin: 0;
          }
          .invoice-subtitle { 
            font-size: 14px; 
            color: #666; 
            margin-top: 5px;
          }
          .invoice-number { 
            font-size: 18px; 
            font-weight: bold; 
            text-align: right;
          }
          .invoice-status {
            background: #1890ff;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            margin-top: 8px;
          }
          .company-info { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 30px;
          }
          .company-section { 
            width: 45%; 
            background: #f8f9fa; 
            padding: 16px; 
            border-radius: 6px;
          }
          .company-section h4 { 
            color: #666; 
            margin-bottom: 12px; 
            font-size: 14px;
          }
          .company-name { 
            font-weight: bold; 
            margin-bottom: 8px; 
            font-size: 16px;
          }
          .invoice-details { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 30px;
            background: #fafafa;
            padding: 16px;
            border-radius: 6px;
          }
          .detail-item { 
            text-align: center;
          }
          .detail-label { 
            color: #666; 
            font-size: 12px; 
            margin-bottom: 4px;
          }
          .detail-value { 
            font-weight: bold; 
            font-size: 14px;
          }
          .items-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 30px;
            border: 1px solid #e8e8e8;
            border-radius: 6px;
            overflow: hidden;
          }
          .items-table th { 
            background: #f5f5f5; 
            padding: 12px; 
            text-align: left; 
            font-weight: bold;
            border-bottom: 1px solid #e8e8e8;
          }
          .items-table td { 
            padding: 12px; 
            border-bottom: 1px solid #f0f0f0;
          }
          .items-table tr:last-child td { 
            border-bottom: none;
          }
          .text-right { 
            text-align: right;
          }
          .text-center { 
            text-align: center;
          }
          .totals { 
            width: 100%; 
            margin-top: 20px;
          }
          .totals-section { 
            float: right; 
            width: 300px; 
            background: #fafafa; 
            padding: 20px; 
            border-radius: 6px;
            border: 1px solid #e8e8e8;
          }
          .total-row { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 8px;
          }
          .total-final { 
            font-size: 18px; 
            font-weight: bold; 
            color: #1890ff; 
            border-top: 1px solid #ddd; 
            padding-top: 12px; 
            margin-top: 12px;
          }
          .notes { 
            clear: both; 
            margin-top: 30px; 
            background: #f8f9fa; 
            padding: 16px; 
            border-radius: 6px;
          }
          .notes h4 { 
            color: #666; 
            margin-bottom: 12px;
          }
          @media print {
            body { margin: 0; padding: 15px; }
            .invoice-header { page-break-after: avoid; }
            .company-info { page-break-after: avoid; }
            .items-table { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <div>
            <h1 class="invoice-title">發票</h1>
            <div class="invoice-subtitle">INVOICE</div>
          </div>
          <div>
            <div class="invoice-number">${generatedInvoice.invoiceNumber}</div>
            <div class="invoice-status">已發送</div>
          </div>
        </div>

        <div class="company-info">
          <div class="company-section">
            <h4>開票方</h4>
            <div class="company-name">Your Company Name</div>
            <div>123 Business Street</div>
            <div>Taipei, Taiwan 10048</div>
            <div>電話: +886-2-1234-5678</div>
            <div>統編: 12345678</div>
          </div>
          <div class="company-section">
            <h4>收票方</h4>
            <div class="company-name">${selectedCustomer?.name || ''}</div>
            <div>${selectedCustomer?.address || ''}</div>
            <div>${selectedCustomer?.city || ''}, ${selectedCustomer?.country || ''}</div>
            <div>電話: ${selectedCustomer?.phone || ''}</div>
            <div>聯絡人: ${selectedCustomer?.contactPerson || ''}</div>
          </div>
        </div>

        <div class="invoice-details">
          <div class="detail-item">
            <div class="detail-label">發票日期</div>
            <div class="detail-value">${generatedInvoice.generatedAt.toLocaleDateString('zh-TW')}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">到期日期</div>
            <div class="detail-value">${generatedInvoice.dueDate.toLocaleDateString('zh-TW')}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">付款條件</div>
            <div class="detail-value">${form.getFieldValue('paymentTerms') || 'Net 30'}</div>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th>項目描述</th>
              <th class="text-center" style="width: 80px;">數量</th>
              <th class="text-right" style="width: 100px;">單價</th>
              <th class="text-right" style="width: 100px;">折扣</th>
              <th class="text-right" style="width: 120px;">小計</th>
            </tr>
          </thead>
          <tbody>
            ${orderItems.map(item => `
              <tr>
                <td>
                  <div style="font-weight: bold;">${item.product.name}</div>
                  <div style="font-size: 12px; color: #666;">SKU: ${item.product.sku}</div>
                </td>
                <td class="text-center">${item.quantity}</td>
                <td class="text-right">$${item.unitPrice.toFixed(2)}</td>
                <td class="text-right">${item.discountType === 'percentage' ? `${item.discount}%` : `$${item.discount}`}</td>
                <td class="text-right" style="font-weight: bold;">$${item.lineTotal.toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="totals-section">
            <div class="total-row">
              <span>小計:</span>
              <span>$${calculateTotals().subtotal.toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>折扣:</span>
              <span>-$${calculateTotals().discountAmount.toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>稅費:</span>
              <span>$${calculateTotals().taxAmount.toFixed(2)}</span>
            </div>
            <div class="total-row total-final">
              <span>總計:</span>
              <span>$${generatedInvoice.amount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        ${form.getFieldValue('terms') ? `
          <div class="notes">
            <h4>付款條款及備註</h4>
            <div style="white-space: pre-line; font-size: 13px; line-height: 1.6;">
              ${form.getFieldValue('terms')}
            </div>
          </div>
        ` : ''}
      </body>
      </html>
    `;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          // Close the window after printing (optional)
          printWindow.onafterprint = () => {
            printWindow.close();
          };
        }, 500);
      };
      
      message.success('正在準備列印發票...');
    } else {
      message.error('無法打開列印窗口，請檢查瀏覽器彈窗設置');
    }
  };

  const handleGenerateInvoice = () => {
    if (orderItems.length === 0) {
      message.warning('此訂單沒有產品項目，無法生成發票');
      return;
    }

    if (invoiceGenerated) {
      message.info('此訂單已生成發票，無法重複生成');
      return;
    }

    const totals = calculateTotals();
    const formValues = form.getFieldsValue();
    
    // Simulate invoice generation
    Modal.confirm({
      title: '生成發票確認',
      content: (
        <div>
          <p>確定要為訂單 <strong>{formValues.orderNumber}</strong> 生成發票嗎？</p>
          <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', margin: '12px 0' }}>
            <div><strong>客戶：</strong>{selectedCustomer?.name}</div>
            <div><strong>訂單金額：</strong>${totals.totalAmount.toFixed(2)}</div>
            <div><strong>產品數量：</strong>{orderItems.length} 項</div>
          </div>
          <p style={{ color: '#f5222d', fontWeight: 'bold' }}>
            ⚠️ 注意：發票生成後不可撤銷，請確認訂單信息無誤。
          </p>
          <p>生成後將自動創建發票記錄並發送給客戶。</p>
        </div>
      ),
      okText: '確認生成',
      cancelText: '取消',
      onOk() {
        // Simulate invoice generation process
        const invoiceNumber = `INV-${new Date().getFullYear()}${(new Date().getMonth() + 1).toString().padStart(2, '0')}${Date.now().toString().slice(-6)}`;
        message.loading('正在生成發票...', 1.5);
        
        setTimeout(() => {
          const invoice = {
            invoiceNumber,
            generatedAt: new Date(),
            amount: totals.totalAmount,
            dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
            status: 'sent' as const
          };
          
          setInvoiceGenerated(true);
          setGeneratedInvoice(invoice);
          
          // 顯示成功消息并提供更多操作
          Modal.success({
            title: '發票生成成功！',
            content: (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <div><strong>發票號碼：</strong>{invoiceNumber}</div>
                  <div><strong>生成時間：</strong>{invoice.generatedAt.toLocaleString('zh-TW')}</div>
                  <div><strong>發票金額：</strong>${invoice.amount.toFixed(2)}</div>
                </div>
                <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', padding: '12px', borderRadius: '4px' }}>
                  <div style={{ color: '#52c41a', fontWeight: 'bold', marginBottom: '8px' }}>✅ 發票已成功生成並保存</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    • 發票記錄已添加到系統<br/>
                    • 客戶將收到發票通知郵件<br/>
                    • 可在發票管理中查看詳細信息
                  </div>
                </div>
              </div>
            ),
            onOk() {
              // TODO: Navigate to invoice view or refresh the page
              console.log('Invoice generated:', invoice);
            }
          });
          
          // Here you would typically:
          // 1. Call API to create invoice
          // 2. Update order status to 'invoiced' 
          // 3. Send email to customer
          // 4. Navigate to invoice view
        }, 1500);
      }
    });
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchProduct.toLowerCase()) ||
    product.barcode?.includes(searchProduct) ||
    product.alternateSkuList?.some(sku => sku.toLowerCase().includes(searchProduct.toLowerCase()))
  );

  const orderItemColumns: ColumnsType<SalesOrderItem> = [
    {
      title: '產品資訊',
      key: 'product',
      width: 300,
      render: (_, record) => (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
            {record.product.name}
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
            <span>SKU: {record.product.sku}</span>
            <span>庫存: {record.product.stockQuantity} {record.product.uom}</span>
          </div>
          {record.product.description && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              {record.product.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: '數量',
      key: 'quantity',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <InputNumber
          min={0}
          max={record.product.stockQuantity}
          value={record.quantity}
          onChange={(value) => updateItemQuantity(record.id, value || 0)}
          disabled={mode === 'view'}
          style={{ width: '80px' }}
          controls={mode !== 'view'}
        />
      )
    },
    {
      title: '單價 ($)',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.unitPrice}
          onChange={(value) => updateItemPrice(record.id, value || 0)}
          disabled={mode === 'view'}
          style={{ width: '100px' }}
        />
      )
    },
    {
      title: '折扣',
      key: 'discount',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Space.Compact style={{ width: '100%' }}>
          <InputNumber
            min={0}
            max={record.discountType === 'percentage' ? 100 : undefined}
            value={record.discount}
            onChange={(value) => updateItemDiscount(record.id, value || 0, record.discountType)}
            style={{ width: '70%' }}
            disabled={mode === 'view'}
          />
          <Select
            value={record.discountType}
            onChange={(value) => updateItemDiscount(record.id, record.discount, value)}
            style={{ width: '30%' }}
            disabled={mode === 'view'}
          >
            <Option value="percentage">%</Option>
            <Option value="fixed">$</Option>
          </Select>
        </Space.Compact>
      )
    },
    {
      title: '小計 ($)',
      key: 'lineTotal',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
          ${record.lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </Text>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="刪除此產品">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeItem(record.id)}
            disabled={mode === 'view'}
            size="small"
          />
        </Tooltip>
      )
    }
  ];

  const totals = calculateTotals();

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header Card */}
        <Card style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              {mode === 'create' ? <PlusOutlined style={{ marginRight: 8 }} /> : mode === 'edit' ? <EditOutlined style={{ marginRight: 8 }} /> : <EyeOutlined style={{ marginRight: 8 }} />}
              {mode === 'create' ? '創建銷售訂單' : mode === 'edit' ? '編輯銷售訂單' : '查看銷售訂單'}
            </Title>
            <Text type="secondary">
              {mode === 'create' ? '填寫訂單資訊並添加產品' : mode === 'edit' ? '修改訂單資訊和產品明細' : '查看訂單詳細資訊'}
            </Text>
          </Col>
          <Col>
            <Space size="middle">
              {mode !== 'view' && (
                <>
                  <Button size="large" onClick={onCancel}>
                    取消
                  </Button>
                  <Button
                    size="large"
                    icon={<SaveOutlined />}
                    onClick={() => handleSave('draft')}
                    loading={loading}
                    style={{ minWidth: '120px' }}
                  >
                    保存草稿
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<SendOutlined />}
                    onClick={() => handleSave('confirmed')}
                    loading={loading}
                    style={{ minWidth: '120px' }}
                  >
                    確認訂單
                  </Button>
                </>
              )}
              {mode === 'view' && (
                <Space direction="vertical" align="end">
                  {invoiceGenerated && generatedInvoice ? (
                    <div style={{ textAlign: 'right', marginBottom: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#52c41a', marginBottom: '4px' }}>
                        ✅ 已生成發票
                      </div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        {generatedInvoice.invoiceNumber}
                      </div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        {generatedInvoice.generatedAt.toLocaleString('zh-TW', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  ) : null}
                  
                  <Button 
                    type={invoiceGenerated ? "default" : "primary"}
                    size="large" 
                    icon={<FileTextOutlined />}
                    onClick={handleGenerateInvoice}
                    disabled={invoiceGenerated}
                  >
                    {invoiceGenerated ? '已生成發票' : '生成發票'}
                  </Button>
                  
                  {invoiceGenerated && (
                    <Button 
                      size="large"
                      icon={<EyeOutlined />}
                      style={{ marginTop: '8px' }}
                      onClick={() => setInvoiceViewVisible(true)}
                    >
                      查看發票
                    </Button>
                  )}
                </Space>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Order Information Card */}
      <Card 
        title={
          <Space>
            <FileTextOutlined />
            <span>訂單基本資訊</span>
          </Space>
        }
        style={{ 
          marginBottom: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Form form={form} layout="vertical">
          {/* 第一行：訂單編號、訂單日期 */}
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                label="訂單編號"
                name="orderNumber"
                rules={[{ required: true, message: '訂單編號不能為空' }]}
              >
                <Input
                  placeholder="自動生成"
                  prefix={<FileTextOutlined />}
                  disabled
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="訂單日期"
                name="orderDate"
                rules={[{ required: true, message: '請選擇訂單日期' }]}
                initialValue={moment()}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  size="large"
                  disabled={mode === 'view'}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 第二行：發貨倉庫、預期交付日期 */}
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                label="發貨倉庫"
                name="warehouseId"
                rules={[{ required: true, message: '請選擇發貨倉庫' }]}
              >
                <Select
                  placeholder="選擇倉庫"
                  onChange={handleWarehouseChange}
                  disabled={mode === 'view'}
                  size="large"
                >
                  {warehouses.map(warehouse => (
                    <Option key={warehouse.id} value={warehouse.id}>
                      <Space>
                        <ShopOutlined />
                        <span>{warehouse.name} ({warehouse.code})</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="預期交付日期"
                name="deliveryDate"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  size="large"
                  disabled={mode === 'view'}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 銷售平台 */}
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                label="銷售平台"
                name="salesPlatform"
              >
                <Select
                  placeholder="選擇銷售渠道"
                  disabled={mode === 'view'}
                  size="large"
                  allowClear
                >
                  <Option value="HKTV Mall">HKTV Mall</Option>
                  <Option value="Shopify">Shopify</Option>
                  <Option value="Offline Store">Offline Store</Option>
                  <Option value="Amazon">Amazon</Option>
                  <Option value="eBay">eBay</Option>
                  <Option value="Taobao">Taobao</Option>
                  <Option value="Tmall">Tmall</Option>
                  <Option value="JD.com">JD.com</Option>
                  <Option value="Pinduoduo">Pinduoduo</Option>
                  <Option value="Lazada">Lazada</Option>
                  <Option value="Shopee">Shopee</Option>
                  <Option value="Direct Sales">Direct Sales</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 第三行：客戶、付款条件 */}
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                label="客戶"
                name="customerId"
                rules={[{ required: true, message: '請選擇客戶' }]}
              >
                <Select
                  placeholder="選擇客戶"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleCustomerChange}
                  disabled={mode === 'view'}
                  size="large"
                  optionLabelProp="label"
                >
                  {customers.map(customer => (
                    <Option 
                      key={customer.id} 
                      value={customer.id}
                      label={customer.name}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{customer.name}</div>
                        <div style={{ color: '#666', fontSize: '12px' }}>
                          {customer.email} • {customer.phone}
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="付款條件"
                name="paymentTerms"
                initialValue="Net 30"
              >
                <Select disabled={mode === 'view'} size="large">
                  <Option value="Net 15">Net 15 天</Option>
                  <Option value="Net 30">Net 30 天</Option>
                  <Option value="Net 60">Net 60 天</Option>
                  <Option value="COD">貨到付款</Option>
                  <Option value="Prepaid">預付款</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 第四行：優先級、客戶信息 */}
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                label="優先級"
                name="priority"
                initialValue="normal"
              >
                <Select disabled={mode === 'view'} size="large">
                  <Option value="low">
                    <Tag color="green">低</Tag>
                  </Option>
                  <Option value="normal">
                    <Tag color="blue">普通</Tag>
                  </Option>
                  <Option value="high">
                    <Tag color="orange">高</Tag>
                  </Option>
                  <Option value="urgent">
                    <Tag color="red">緊急</Tag>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {selectedCustomer && (
                <div style={{ paddingTop: '8px' }}>
                  <div style={{ marginBottom: '8px', color: '#666', fontSize: '14px' }}>客戶聯絡人</div>
                  <Card size="small" style={{ background: '#f8f9fa', border: '1px solid #e8e8e8' }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        <Text strong style={{ fontSize: '14px' }}>{selectedCustomer.contactPerson}</Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {selectedCustomer.phone} • {selectedCustomer.email}
                      </Text>
                    </Space>
                  </Card>
                </div>
              )}
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Order Items Card */}
      <Card 
        title={
          <Space>
            <BarcodeOutlined />
            <span>產品明細</span>
          </Space>
        }
        extra={
          mode !== 'view' && (
            <Button
              type="primary"
              icon={<BarcodeOutlined />}
              onClick={() => {
                setProductSearchModalVisible(true);
                // 首次打開沒有關鍵詞時也拉取前 10 條，提供即刻可選
                if (!products.length && !searchProduct) {
                  setSearchProduct('');
                }
              }}
              size="large"
            >
              添加產品
            </Button>
          )
        }
        style={{ 
          marginBottom: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Table
          dataSource={orderItems}
          columns={orderItemColumns}
          pagination={false}
          rowKey="id"
          size="middle"
          locale={{
            emptyText: (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <BarcodeOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <div style={{ fontSize: '16px', color: '#666' }}>
                  {mode === 'view' ? '此訂單暫無產品' : '點擊右上角"添加產品"按鈕開始創建訂單'}
                </div>
              </div>
            )
          }}
          style={{ minHeight: '200px' }}
        />
      </Card>

      {/* Order Summary */}
      {orderItems.length > 0 && (
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            {/* Notes Section will go here */}
          </Col>
          <Col xs={24} lg={8}>
            <Card 
              title={
                <Space>
                  <CalculatorOutlined />
                  <span>訂單匯總</span>
                </Space>
              }
              style={{ 
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Row justify="space-between">
                  <Text>小計</Text>
                  <Text strong>${totals.subtotal.toFixed(2)}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>折扣</Text>
                  <Text type="danger">-${totals.discountAmount.toFixed(2)}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>稅費</Text>
                  <Text>${totals.taxAmount.toFixed(2)}</Text>
                </Row>
                <Divider style={{ margin: '8px 0' }} />
                <Row justify="space-between">
                  <Text strong style={{ fontSize: '18px' }}>總計</Text>
                  <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
                    ${totals.totalAmount.toFixed(2)}
                  </Text>
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* Notes and Terms Card */}
      <Card 
        title={
          <Space>
            <FileTextOutlined />
            <span>備注与条款</span>
          </Space>
        }
        style={{ 
          marginTop: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item 
                label="內部備注" 
                name="notes"
                style={{ marginBottom: '24px' }}
              >
                <TextArea
                  rows={4}
                  placeholder="訂單的內部備注信息，不会顯示给客戶"
                  disabled={mode === 'view'}
                  style={{ fontSize: '14px' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item 
                label="條款與條件"
                name="terms"
              >
                <TextArea
                  rows={4}
                  placeholder="訂單的條款與條件，会顯示在訂單文檔中"
                  disabled={mode === 'view'}
                  style={{ fontSize: '14px' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Product Search Modal */}
      <Modal
        title={
          <Space>
            <BarcodeOutlined />
            <span>選擇產品</span>
          </Space>
        }
        open={productSearchModalVisible}
        onCancel={() => setProductSearchModalVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Input
              placeholder="搜索產品名称、SKU或条碼"
              prefix={<SearchOutlined />}
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              allowClear
              size="large"
              style={{ marginBottom: '16px' }}
            />
            <div style={{ fontSize: '12px', color: '#666' }}>
              {productLoading ? '正在加載產品...' : `找到 ${filteredProducts.length} 個產品，點擊產品卡片添加到訂單`}
            </div>
          </div>
          
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {productLoading ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <Spin />
              </div>
            ) : filteredProducts.length > 0 ? (
              <Row gutter={[16, 16]}>
                {filteredProducts.map(product => (
                  <Col xs={24} sm={12} key={product.id}>
                    <Card
                      size="small"
                      hoverable
                      onClick={() => addProduct(product)}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '1px solid #d9d9d9'
                      }}
                      bodyStyle={{ padding: '16px' }}
                      className="product-card"
                    >
                      <Row justify="space-between" align="top">
                        <Col flex="1" style={{ paddingRight: '16px' }}>
                          <div style={{ marginBottom: '8px' }}>
                            <Text strong style={{ fontSize: '14px', display: 'block' }}>
                              {product.name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              SKU: {product.sku}
                            </Text>
                            {product.alternateSkuList && product.alternateSkuList.length > 0 && (
                              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                備用 SKU: {product.alternateSkuList.join(', ')}
                              </Text>
                            )}
                          </div>
                          
                          <div style={{ marginBottom: '8px' }}>
                            <Space size="small">
                              <Tag color="blue">
                                {product.category}
                              </Tag>
                              <Tag color={product.stockQuantity > 10 ? 'green' : product.stockQuantity > 0 ? 'orange' : 'red'}>
                                庫存: {product.stockQuantity} {product.uom}
                              </Tag>
                            </Space>
                          </div>
                          
                          {product.description && (
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              {product.description}
                            </Text>
                          )}
                        </Col>
                        
                        <Col>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                              ${product.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </div>
                            <Button 
                              type="primary" 
                              size="small"
                              icon={<PlusOutlined />}
                              disabled={product.stockQuantity === 0}
                            >
                              添加
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <SearchOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <div style={{ fontSize: '16px', color: '#666' }}>
                  {searchProduct ? '未找到匹配的產品' : '請輸入搜索關键词'}
                </div>
              </div>
            )}
          </div>
        </Space>
      </Modal>

      {/* Invoice View Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            <span>發票詳情</span>
          </Space>
        }
        open={invoiceViewVisible}
        onCancel={() => setInvoiceViewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInvoiceViewVisible(false)}>
            關閉
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={handlePrintInvoice}
          >
            列印發票
          </Button>,
          <Button key="email" icon={<MailOutlined />}>
            發送郵件
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {generatedInvoice && (
          <div style={{ padding: '20px 0' }}>
            {/* Invoice Header */}
            <div style={{ 
              borderBottom: '2px solid #1890ff', 
              paddingBottom: '20px', 
              marginBottom: '30px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                  發票
                </Title>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                  INVOICE
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {generatedInvoice.invoiceNumber}
                </div>
                <Tag color={
                  generatedInvoice.status === 'paid' ? 'green' :
                  generatedInvoice.status === 'sent' ? 'blue' :
                  generatedInvoice.status === 'overdue' ? 'red' : 'default'
                }>
                  {generatedInvoice.status === 'paid' ? '已付款' :
                   generatedInvoice.status === 'sent' ? '已發送' :
                   generatedInvoice.status === 'overdue' ? '逾期' : '草稿'}
                </Tag>
              </div>
            </div>

            {/* Invoice Details */}
            <Row gutter={[32, 16]}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: '24px' }}>
                  <Title level={5} style={{ color: '#666', marginBottom: '12px' }}>
                    開票方
                  </Title>
                  <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '6px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      Your Company Name
                    </div>
                    <div>123 Business Street</div>
                    <div>Taipei, Taiwan 10048</div>
                    <div>電話: +886-2-1234-5678</div>
                    <div>統編: 12345678</div>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: '24px' }}>
                  <Title level={5} style={{ color: '#666', marginBottom: '12px' }}>
                    收票方
                  </Title>
                  <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '6px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      {selectedCustomer?.name}
                    </div>
                    <div>{selectedCustomer?.address}</div>
                    <div>{selectedCustomer?.city}, {selectedCustomer?.country}</div>
                    <div>電話: {selectedCustomer?.phone}</div>
                    <div>聯絡人: {selectedCustomer?.contactPerson}</div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[32, 16]} style={{ marginBottom: '30px' }}>
              <Col xs={24} sm={8}>
                <div>
                  <div style={{ color: '#666', fontSize: '12px' }}>發票日期</div>
                  <div style={{ fontWeight: 'bold' }}>
                    {generatedInvoice.generatedAt.toLocaleDateString('zh-TW')}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div>
                  <div style={{ color: '#666', fontSize: '12px' }}>到期日期</div>
                  <div style={{ fontWeight: 'bold' }}>
                    {generatedInvoice.dueDate.toLocaleDateString('zh-TW')}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div>
                  <div style={{ color: '#666', fontSize: '12px' }}>付款條件</div>
                  <div style={{ fontWeight: 'bold' }}>
                    {form.getFieldValue('paymentTerms') || 'Net 30'}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Invoice Items */}
            <div style={{ marginBottom: '30px' }}>
              <Title level={5} style={{ color: '#666', marginBottom: '16px' }}>
                項目明細
              </Title>
              <Table
                dataSource={orderItems}
                columns={[
                  {
                    title: '項目描述',
                    key: 'description',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{record.product.name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          SKU: {record.product.sku}
                        </div>
                      </div>
                    )
                  },
                  {
                    title: '數量',
                    dataIndex: 'quantity',
                    width: 80,
                    align: 'center'
                  },
                  {
                    title: '單價',
                    dataIndex: 'unitPrice',
                    width: 100,
                    align: 'right',
                    render: (price) => `$${price.toFixed(2)}`
                  },
                  {
                    title: '折扣',
                    key: 'discount',
                    width: 100,
                    align: 'right',
                    render: (_, record) => 
                      record.discountType === 'percentage' 
                        ? `${record.discount}%`
                        : `$${record.discount}`
                  },
                  {
                    title: '小計',
                    dataIndex: 'lineTotal',
                    width: 120,
                    align: 'right',
                    render: (total) => (
                      <span style={{ fontWeight: 'bold' }}>
                        ${total.toFixed(2)}
                      </span>
                    )
                  }
                ]}
                pagination={false}
                size="small"
                style={{ 
                  border: '1px solid #e8e8e8',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Invoice Total */}
            <div style={{ 
              background: '#fafafa', 
              padding: '20px', 
              borderRadius: '6px',
              border: '1px solid #e8e8e8'
            }}>
              <Row justify="end">
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>小計:</span>
                      <span>${calculateTotals().subtotal.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>折扣:</span>
                      <span>-${calculateTotals().discountAmount.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>稅費:</span>
                      <span>${calculateTotals().taxAmount.toFixed(2)}</span>
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      color: '#1890ff'
                    }}>
                      <span>總計:</span>
                      <span>${generatedInvoice.amount.toFixed(2)}</span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Invoice Notes */}
            {form.getFieldValue('terms') && (
              <div style={{ marginTop: '30px' }}>
                <Title level={5} style={{ color: '#666', marginBottom: '12px' }}>
                  付款條款及備註
                </Title>
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '6px',
                  whiteSpace: 'pre-line',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  {form.getFieldValue('terms')}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      </div>
    </div>
  );
};

export default SalesOrderBuilder;
